import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useLazyGetBranchesForSystemQuery } from '@app/api/Branches';
import { useLazyGetClientServicesQuery } from '@app/api/ClientServices';
import { useLazySearchCrmTaskUsersQuery } from '@app/api/CrmTasks';
import {
  useLazyGetRobotAttachmentsListQuery,
  useUploadRobotAttachmentMutation,
} from '@app/api/RobotAttachments';
import type { ModelRobotAttachment } from '@app/api/RobotAttachments';
import { useLazyGetTagsQuery } from '@app/api/Tags';
import { useLazyGetEmployeesListQuery } from '@app/api/Users';
import { MESSAGE_MODE_AI } from '@app/components/MessageInputWithFilesUpload/data/messageMode';
import {
  INCOMING_MESSAGE_TRIGGER_TYPE,
  KEYWORD_MESSAGE_ROBOT_INDEX,
  KEYWORD_MESSAGE_TRIGGER_TYPE,
  SEND_MESSAGE_ROBOT_INDEX,
  SET_TAG_ROBOT_INDEX,
  TASK_CREATING_ROBOT_INDEX,
  TRANSITION_FROM_THE_SALES_FUNNEL_ROBOT_INDEX,
  TRANSITION_FROM_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX,
  TRANSITION_TO_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX,
} from '@app/components/Modals/BotDetailsModal/constants/botIndexes';
import type {
  BotDetailsFormInterface,
  BotDetailsType,
  BotTrigger,
} from '@app/components/Modals/BotDetailsModal/types';
import { getCommentReplyTypeParameters } from '@app/components/Modals/BotDetailsModal/utils/commentReply';
import { getFilledKeywordGroups } from '@app/components/Modals/BotDetailsModal/utils/keywordGroups';
import { getKeywordMessageConditionParameters } from '@app/components/Modals/BotDetailsModal/utils/keywordOverlaps';
import { mapTriggerTagsToApi } from '@app/components/Modals/BotDetailsModal/utils/triggerTagsGroups';
import type { UseSalesFunnelColumnComponentProps } from '@app/components/SalesFunnelColumnComponent/types';
import { useSchedule } from '@app/components/Schedule';
import {
  DEFAULT_CHANNEL_INDEX,
  DEFAULT_SOCIAL_INTEGRATION_ID_INDEX,
  FRONT_WARNING,
  INSTAGRAM,
  SUCCESS,
} from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useForceCloseModal } from '@app/providers/CheckNetworkProvider/CheckNetworkProvider';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { useLazyListQuery } from '@app/services/rtkQuery/SalesFunnels/api';
import {
  useCreateRobotMutation,
  useDeleteRobotMutation,
  useDeleteSalesFunnelStageMutation,
  useLazyGetRobotForIdQuery,
  useLazyGetRobotsListTriggersQuery,
  useLazyGetRobotsListTypesQuery,
  useLazyGetSalesFunnelStagesListForIdQuery,
  useUpdateRobotMutation,
} from '@app/services/rtkQuery/SalesFunnelsStages/api';
import type { NSalesFunnelsStagesApi } from '@app/services/rtkQuery/SalesFunnelsStages/types';
import { useLazyPrepareSendoutCreationQuery } from '@app/services/rtkQuery/Sendouts/api';
import type { NSendoutsApi } from '@app/services/rtkQuery/Sendouts/types';
import type { NEmployees, NFunnels, NTags } from '@app/types';

import { LAST_OWNER } from '../constants';

import type { BaseOptionType } from 'antd/es/select';

interface ListItem {
  id: string;
  title: string;
}

export const useSalesFunnelColumnComponent = ({
  stageFunnelData,
  refetchGetSalesFunnelStages,
  setCurrentSalesFunnelStage,
  handleOpenSalesFunnelModal,
}: UseSalesFunnelColumnComponentProps) => {
  const [query] = useSearchParams();
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const salesFunnelId = query.get('id');
  const { Id } = stageFunnelData;
  const { getScheduleDateForRobots } = useSchedule();

  const [botDetailsModalType, setBotDetailsModalType] =
    useState<BotDetailsType>('create');
  const [selectedBotType, setSelectedBotType] = useState<ListItem | null>(null);
  const [triggerList, setTriggerList] = useState<BotTrigger[]>([]);
  const [salesFunnelsList, setSalesFunnelsList] = useState<
    BaseOptionType[] | null
  >([]);
  const [taskUsersList, setTaskUsersList] = useState<BaseOptionType[] | null>(
    []
  );
  const [salesFunnelStagesList, setSalesFunnelStagesList] = useState<
    Array<ListItem>
  >([]);
  const [selectedBotFiles, setSelectedBotFiles] = useState<
    ModelRobotAttachment[]
  >([]);
  const [isOpenSelectTypeBotModal, setIsOpenSelectTypeBotModal] =
    useState(false);
  const [employees, setEmployees] = useState<BaseOptionType[] | null>([]);
  const [botTypeList, setBotTypeList] = useState<Array<ListItem>>([]);
  const [servicesList, setServicesList] = useState<
    NEmployees.ModelClientService[]
  >([]);
  const [socialIntegrations, setSocialIntegrations] = useState<
    BaseOptionType[] | null
  >([]);
  const [tags, setTags] = useState<NTags.Tag[]>([]);
  const [allBranches, setAllBranches] = useState<BaseOptionType[] | null>([]);
  const [isOpenBotDetailsModal, setIsOpenBotDetailsModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<'stage' | 'bot' | null>(
    null
  );
  const [selectedBot, setSelectedBot] = useState<{
    details: NSalesFunnelsStagesApi.GetRobotForIdResponse;
    bot: NFunnels.SalesFunnelRobotDto;
  } | null>(null);
  const [isEditBotModalLoading, setIsEditBotModalLoading] = useState(false);
  const [isBotDetailsModalDataLoading, setIsBotDetailsModalDataLoading] =
    useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);

  const {
    userAccessRights: {
      SalesFunnels: { IsStageCreate, IsStageEdit, IsStageDelete },
    },
  } = useNewUserAccessRights();

  const [createBot] = useCreateRobotMutation();
  const [deleteSalesFunnelStage] = useDeleteSalesFunnelStageMutation();
  const [deleteBot] = useDeleteRobotMutation();
  const [updateBot] = useUpdateRobotMutation();
  const [uploadAttachments] = useUploadRobotAttachmentMutation();

  const [getRobotsListTypes, robotsListTypesState] =
    useLazyGetRobotsListTypesQuery();
  const [getRobotsListTriggers, robotsListTriggersState] =
    useLazyGetRobotsListTriggersQuery();
  const [getSalesFunnelStagesListForId] =
    useLazyGetSalesFunnelStagesListForIdQuery();
  const [getSalesFunnelList] = useLazyListQuery();
  const [getEmployeesListQuery] = useLazyGetEmployeesListQuery();
  const [getBotForId] = useLazyGetRobotForIdQuery();
  const [getRobotFiles] = useLazyGetRobotAttachmentsListQuery();
  const [getPrepareSendoutCreation] = useLazyPrepareSendoutCreationQuery();
  const [getClientServices] = useLazyGetClientServicesQuery();
  const [searchCrmTaskUsers] = useLazySearchCrmTaskUsersQuery();
  const [getBranchesForSystem] = useLazyGetBranchesForSystemQuery();
  const [getTags] = useLazyGetTagsQuery();

  const closeBotDetailsModal = useCallback(() => {
    setTriggerList([]);
    setSelectedBot(null);
    setIsOpenBotDetailsModal(false);
  }, []);

  useForceCloseModal(closeBotDetailsModal, isOpenBotDetailsModal);

  const getBotForIdHandler = useCallback(
    async (id: number) => {
      try {
        const bot = await getBotForId({ ActiveBranchId, id }).unwrap();

        return bot;
      } catch (error) {
        showNotification(error);
      }
    },
    [ActiveBranchId, getBotForId, showNotification]
  );

  const getBotTypesHandler = useCallback(async () => {
    try {
      const list = await getRobotsListTypes({ ActiveBranchId }).unwrap();

      const formattedList = Object.keys(list).map((key) => ({
        id: key,
        title: list[key],
      }));

      setBotTypeList(formattedList);
    } catch (error) {
      showNotification(error);
    }
  }, [ActiveBranchId, getRobotsListTypes, showNotification]);

  const deleteBotHandler = useCallback(
    async (id?: number) => {
      if (!id) return showNotification('Робот не найден', FRONT_WARNING);

      try {
        await deleteBot({
          ActiveBranchId,
          id,
        }).unwrap();

        showNotification('Робот успешно удален', SUCCESS);

        refetchGetSalesFunnelStages?.();
        closeBotDetailsModal();
        setSelectedBot(null);
        setIsOpenDeleteModal(false);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      deleteBot,
      refetchGetSalesFunnelStages,
      showNotification,
      closeBotDetailsModal,
    ]
  );

  const updateBotHandler = useCallback(
    async ({
      id,
      data,
    }: {
      id: number;
      data: NSalesFunnelsStagesApi.UpdateRobotRequest;
    }) => {
      try {
        await updateBot({
          ActiveBranchId,
          id,
          data,
        }).unwrap();

        showNotification('Робот успешно изменен', 'success');

        refetchGetSalesFunnelStages?.();
        closeBotDetailsModal();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      refetchGetSalesFunnelStages,
      showNotification,
      updateBot,
      closeBotDetailsModal,
    ]
  );

  const createBotHandler = useCallback(
    async (
      data: NSalesFunnelsStagesApi.UpdateRobotRequest,
      applyToExistingLeads: boolean | undefined
    ) => {
      try {
        await createBot({
          ActiveBranchId,
          data: {
            ...data,
            ApplyToExistingLeads: applyToExistingLeads,
          },
        }).unwrap();

        showNotification('Робот успешно создан', 'success');

        closeBotDetailsModal();
        refetchGetSalesFunnelStages?.();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      createBot,
      refetchGetSalesFunnelStages,
      showNotification,
      closeBotDetailsModal,
    ]
  );

  const openSelectTypeBotModal = useCallback(async () => {
    setIsOpenSelectTypeBotModal(true);
    await getBotTypesHandler();
  }, [getBotTypesHandler]);

  const closeSelectTypeBotModal = useCallback(
    () => setIsOpenSelectTypeBotModal(false),
    []
  );

  const openBotDetailsModal = useCallback(
    () => setIsOpenBotDetailsModal(true),
    []
  );

  const getTriggersHandler = useCallback(
    async (typeId: number, editedRobotId?: number) => {
      try {
        const triggerListData = await getRobotsListTriggers({
          robotType: typeId,
          ActiveBranchId,
        }).unwrap();

        // Бэкенд допускает на этапе только одного робота «Установка тега»
        // с триггером «Ключевые слова во входящем сообщении»
        const hasSetTagIncomingMessageRobot =
          String(typeId) === SET_TAG_ROBOT_INDEX &&
          (stageFunnelData.Robots || []).some(
            (robot) =>
              robot.Id !== editedRobotId &&
              String(robot.Type) === SET_TAG_ROBOT_INDEX &&
              robot.TriggerType === INCOMING_MESSAGE_TRIGGER_TYPE
          );

        const formattedList: BotTrigger[] = Object.keys(triggerListData).map(
          (key) => ({
            id: key,
            title: triggerListData[key],
            ...(hasSetTagIncomingMessageRobot &&
              key === INCOMING_MESSAGE_TRIGGER_TYPE && {
                disabledReason:
                  'На этапе уже настроен робот «Установка тега» с этим триггером. Допускается только один такой робот на этапе',
              }),
          })
        );

        setTriggerList(formattedList);
      } catch (error) {
        showNotification(error);
        closeBotDetailsModal();
      }
    },
    [
      ActiveBranchId,
      getRobotsListTriggers,
      stageFunnelData.Robots,
      closeBotDetailsModal,
      showNotification,
    ]
  );

  const handleGetRobotFiles = useCallback(
    async (id: number) => {
      try {
        const result = await getRobotFiles({
          robotId: id,
          ActiveBranchId,
        }).unwrap();

        setSelectedBotFiles(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [ActiveBranchId, getRobotFiles, showNotification]
  );

  const getSalesFunnelsListHandler = useCallback(async () => {
    try {
      const list = await getSalesFunnelList({
        ActiveBranchId,
      }).unwrap();
      const formattedList = list.map(({ Id, Name }) => ({
        value: Id,
        label: Name,
      }));
      setSalesFunnelsList(formattedList);
    } catch (error) {
      showNotification(error);
    }
  }, [getSalesFunnelList, ActiveBranchId, showNotification]);

  const getTaskUsersHandler = useCallback(async () => {
    try {
      const users = await searchCrmTaskUsers({
        searchTerm: '',
        limit: 0,
        ActiveBranchId,
      }).unwrap();

      const formattedList = [
        {
          value: LAST_OWNER,
          label: 'Последний ответственный',
        },
        ...users.map(({ UserId, NameFirst, NameLast }) => ({
          value: UserId,
          label: `${NameLast || ''} ${NameFirst || ''}`.trim(),
        })),
      ];
      setTaskUsersList(formattedList);
    } catch (error) {
      showNotification(error);
    }
  }, [searchCrmTaskUsers, ActiveBranchId, showNotification]);

  const getSalesFunnelStagesListHandler = useCallback(
    async (customSalesFunnelId?: number) => {
      try {
        const list = await getSalesFunnelStagesListForId({
          ActiveBranchId,
          salesFunnelId: customSalesFunnelId
            ? customSalesFunnelId
            : Number(salesFunnelId),
        }).unwrap();

        const formattedList = list
          .filter(
            (item) =>
              item?.Id !== null && item?.Id !== undefined && item?.Id !== Id
          )
          .map((item) => ({
            id: item?.Id?.toString() ?? '',
            title: item?.Title ?? '',
          }));

        setSalesFunnelStagesList(formattedList);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      salesFunnelId,
      getSalesFunnelStagesListForId,
      Id,
      showNotification,
    ]
  );

  const getEmployees = useCallback(async () => {
    try {
      const list = await getEmployeesListQuery({
        ActiveBranchId,
      }).unwrap();
      const formattedList = list.map(({ Id, NameFirst, NameLast }) => ({
        value: Id,
        label: `${NameLast || ''} ${NameFirst || ''}`,
      }));
      setEmployees(formattedList);
    } catch (error) {
      showNotification(error);
    }
  }, [getEmployeesListQuery, ActiveBranchId, showNotification]);

  const getClientServicesHandler = useCallback(async () => {
    try {
      const servicesListData = await getClientServices({
        ActiveBranchId,
      }).unwrap();

      setServicesList(servicesListData);
    } catch (error) {
      showNotification(error);
    }
  }, [getClientServices, ActiveBranchId, showNotification]);

  const getSocialIntegrations = useCallback(
    async ({
      channelId,
      integrationId,
      tagIds,
    }: Partial<NSendoutsApi.PrepareSendoutCreationRequest>) => {
      if (!ActiveBranchId) return;
      try {
        const handledChannelId = channelId ? { channelId } : {};
        const handledIntegrationId = integrationId ? { integrationId } : {};
        const handledTagIds = tagIds ? { tagIds } : {};

        const { Integrations } = await getPrepareSendoutCreation({
          ActiveBranchId,
          ...handledChannelId,
          ...handledIntegrationId,
          ...handledTagIds,
        }).unwrap();
        const adaptedData = Integrations.map(
          ({ Id, IntegrationName, SocialType, BranchId }) => ({
            value: Id,
            label: IntegrationName,
            type: SocialType,
            branchId: BranchId,
          })
        );

        setSocialIntegrations(adaptedData);
      } catch (error) {
        showNotification(error);
      }
    },
    [getPrepareSendoutCreation, ActiveBranchId, showNotification]
  );

  const getTagsHandler = useCallback(async () => {
    try {
      const tagsResult = await getTags({ ActiveBranchId }).unwrap();
      setTags(
        tagsResult.map((tag) => ({
          ...tag,
          Name: tag.Name ?? '',
          Color: tag.Color ?? '',
          IconId: tag.IconId ?? '',
        }))
      );
    } catch (error) {
      showNotification(error);
    }
  }, [ActiveBranchId, getTags, showNotification]);

  const getBranchesHandler = useCallback(async () => {
    try {
      const response = await getBranchesForSystem({ ActiveBranchId }).unwrap();
      const adaptedData = response.map(({ Id, Name }) => {
        return {
          value: Id,
          label: Name,
        };
      });

      setAllBranches(adaptedData);
    } catch (error) {
      showNotification(error);
    }
  }, [ActiveBranchId, getBranchesForSystem, showNotification]);

  const selectTypeBot = useCallback(
    async (type: { id: string; title: string }) => {
      setSelectedBotType(type);
      setBotDetailsModalType('create');
      setIsBotDetailsModalDataLoading(true);

      await getTriggersHandler(+type.id);
      await getTagsHandler();

      switch (type.id) {
        case TRANSITION_TO_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX:
          await getClientServicesHandler();
          break;
        case TRANSITION_FROM_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX:
          await getSalesFunnelStagesListHandler();
          break;
        case TRANSITION_FROM_THE_SALES_FUNNEL_ROBOT_INDEX:
          await getSalesFunnelsListHandler();
          break;
        case TASK_CREATING_ROBOT_INDEX:
          await getTaskUsersHandler();
          break;
        case SET_TAG_ROBOT_INDEX:
        case KEYWORD_MESSAGE_ROBOT_INDEX:
          await getSocialIntegrations({});
          break;
        case SEND_MESSAGE_ROBOT_INDEX:
          await getSalesFunnelStagesListHandler();
          await getClientServicesHandler();
          await getEmployees();
          await getSocialIntegrations({});
          await getBranchesHandler();
          break;
        default:
          break;
      }

      setIsBotDetailsModalDataLoading(false);
      openBotDetailsModal();
      closeSelectTypeBotModal();
    },
    [
      getTriggersHandler,
      getTagsHandler,
      openBotDetailsModal,
      closeSelectTypeBotModal,
      getClientServicesHandler,
      getSalesFunnelStagesListHandler,
      getTaskUsersHandler,
      getSalesFunnelsListHandler,
      getEmployees,
      getSocialIntegrations,
      getBranchesHandler,
    ]
  );

  const onSaveBotDetails = useCallback(
    async (values: BotDetailsFormInterface) => {
      const {
        trigger,
        deadline,
        comment = '',
        custom_deadline_day = '',
        custom_deadline_time,
        services,
        serviceCategories,
        internalMovement,
        searchPrompt,
        scanPeriod,
        status,
        funnelStage,
        afterExecutionTags,
        applyToExistingLeads,
        tags,
        branch,
        socialIntegrationType,
        socialIntegrationId,
        schedule,
        employees,
        message,
        buttons,
        files,
        socialType,
        useSchedule,
        funnel,
        isNeedRemovePrevFunnel,
        description,
        ownerId,
        task_deadline,
        task_custom_deadline_day = '',
        task_custom_deadline_time,
        setTagIds,
        isNeedAdd,
        conflictingTagIds,
        ignoreSchedule,
        keywordGroups,
        isStopAfterFirstResponse,
        commentReplyText,
        publicationSelectionMode,
        instagramPublicationIds,
        messageMode,
        aiPrompt,
      } = values;

      const isKeywordMessageRobot =
        selectedBotType?.id === KEYWORD_MESSAGE_ROBOT_INDEX;
      const isSetTagIncomingMessageRobot =
        selectedBotType?.id === SET_TAG_ROBOT_INDEX &&
        trigger === INCOMING_MESSAGE_TRIGGER_TYPE;
      const isAiGeneratedMessage =
        (selectedBotType?.id === SEND_MESSAGE_ROBOT_INDEX ||
          isKeywordMessageRobot) &&
        messageMode === MESSAGE_MODE_AI;

      // У триггера "Поиск клиентов через ИИ" нет поля "Срок выполнения" —
      // расписание сканирования задаётся отдельным полем periodicity.
      // Входящее сообщение — событийный триггер, срок бэкенд выставит сам.
      const isTriggerWithoutDeadline =
        trigger === 'AiClientSearch' || isSetTagIncomingMessageRobot;

      let time = custom_deadline_time;
      if (typeof custom_deadline_time === 'string') {
        time = dayjs(custom_deadline_time, 'HH:mm:ss');
      }
      const CustomTimeShift =
        isKeywordMessageRobot || isTriggerWithoutDeadline
          ? null
          : deadline === undefined || custom_deadline_day
          ? `${custom_deadline_day}.${time?.format('HH:mm:ss')}`
          : null;

      let task_time = task_custom_deadline_time;
      if (typeof task_custom_deadline_time === 'string') {
        task_time = dayjs(task_custom_deadline_time, 'HH:mm:ss');
      }
      const CustomTimeDeadline =
        task_deadline === undefined || task_custom_deadline_day
          ? `${task_custom_deadline_day}.${task_time?.format('HH:mm:ss')}`
          : null;
      const getAttachmentsIds = async (): Promise<number[]> => {
        const attachmentsIds: number[] = [];

        if (files && files.length > 0) {
          setIsUploadingAttachments(true);
          for (const file of files) {
            const formData = new FormData();
            const originFile = file.originFileObj;

            if (!originFile) {
              attachmentsIds.push(Number(file.uid));
            }

            if (originFile) {
              formData.append('file', originFile, originFile.name);

              try {
                const result = await uploadAttachments({
                  stageId: Id,
                  ActiveBranchId,
                  data: formData,
                }).unwrap();

                attachmentsIds.push(result);
              } catch (error) {
                showNotification(handleCatchError(error));
              }
            }
          }
          setIsUploadingAttachments(false);
        }

        return attachmentsIds;
      };

      const getTriggerParameters = () => {
        switch (trigger) {
          case 'ByTimer':
            return {};
          case 'ChangeRecordStatus': {
            const handledServices = services || [];

            const triggerData: Record<string, unknown> = {
              Status: status,
              ClientServiceIds: handledServices,
              ServiceIds: handledServices,
            };

            if (serviceCategories && serviceCategories.length) {
              triggerData.CategoryIds = serviceCategories;
            }

            if (typeof internalMovement === 'boolean') {
              triggerData.InternalMovement = internalMovement;
            }

            return selectedBotType?.id === SEND_MESSAGE_ROBOT_INDEX
              ? { ...triggerData, EmployeesIds: employees || [] }
              : triggerData;
          }
          case 'TagIsSet':
            return { TagFilters: mapTriggerTagsToApi(tags) };
          case 'ChannelIsEstablished':
            return {
              SocialType: socialType,
            };
          case 'DateRecord':
            return {
              RecordStatuses: [status],
              ClientServiceIds: services || [],
              // опционально можно тоже дублировать в ServiceIds
              ServiceIds: services || [],
              EmployeeIds: employees || [],
            };
          case INCOMING_MESSAGE_TRIGGER_TYPE: {
            if (!isSetTagIncomingMessageRobot) return {};

            const isChannelRestricted =
              socialIntegrationType &&
              socialIntegrationType !== DEFAULT_CHANNEL_INDEX;

            return {
              KeywordGroups: getFilledKeywordGroups(keywordGroups),
              SocialIntegrationType: isChannelRestricted
                ? socialIntegrationType
                : null,
              SocialIntegrationId:
                isChannelRestricted &&
                socialIntegrationId !== DEFAULT_SOCIAL_INTEGRATION_ID_INDEX
                  ? socialIntegrationId
                  : null,
            };
          }
          case 'AiClientSearch':
            // TriggerParameters отправляются/приходят в PascalCase, как и у
            // остальных триггеров. CurrentFunnelOnly необязателен, но по
            // умолчанию на бэке true — передаём его всегда явно, чтобы
            // неотмеченный чекбокс не превращался на бэке в true.
            return {
              SearchPrompt: searchPrompt,
              ScanPeriod: scanPeriod,
              CurrentFunnelOnly: internalMovement === true,
            };
          default:
            return {};
        }
      };

      const getTypeParameters = async () => {
        switch (selectedBotType?.id) {
          case TRANSITION_TO_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX:
            return {};
          case TRANSITION_FROM_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX:
            return {
              StageToSwitchId: +(funnelStage || 0),
            };
          case TRANSITION_FROM_THE_SALES_FUNNEL_ROBOT_INDEX:
            return {
              SaleFunnelId: funnel,
              StageToSwitchId: +(funnelStage || 0),
              IsNeedRemovePrevFunnel: isNeedRemovePrevFunnel,
            };
          case TASK_CREATING_ROBOT_INDEX:
            return {
              OwnerId: ownerId === LAST_OWNER ? null : ownerId,
              Description: description,
              TimeDeadlineTimeShift: +(deadline || 0),
              ...(CustomTimeDeadline !== null ? { CustomTimeDeadline } : {}),
            };
          case SET_TAG_ROBOT_INDEX:
            return {
              TagIds: setTagIds || [],
              IsNeedAdd: Boolean(isNeedAdd),
              // ResponseType: 0 (Fixed) — AI-классификация пока не поддерживается
              ...(isSetTagIncomingMessageRobot && {
                ConflictingTagIds: conflictingTagIds || [],
                ResponseType: 0,
              }),
            };
          case KEYWORD_MESSAGE_ROBOT_INDEX: {
            const keywordAttachmentsIds = await getAttachmentsIds();
            const isKeywordInstagramChannel =
              socialIntegrationType === INSTAGRAM;
            const filledKeywordButtons = (buttons || []).filter(
              ({ name, url }) => name && url
            );

            return {
              Message: isAiGeneratedMessage ? null : message,
              // Те же условия уходят в проверку пересечений кодовых слов
              ...getKeywordMessageConditionParameters({
                keywordGroups,
                socialIntegrationType,
                socialIntegrationId,
                publicationSelectionMode,
                instagramPublicationIds,
              }),
              IsStopAfterFirstResponse: Boolean(isStopAfterFirstResponse),
              AttachmentsIds: keywordAttachmentsIds,
              Buttons: isKeywordInstagramChannel ? filledKeywordButtons : [],
              ...getCommentReplyTypeParameters(
                commentReplyText,
                socialIntegrationType
              ),
            };
          }
          case SEND_MESSAGE_ROBOT_INDEX:
            const attachmentsIds = await getAttachmentsIds();
            const isInstagramChannel = socialIntegrationType === INSTAGRAM;
            const filledButtons = (buttons || []).filter(
              ({ name, url }) => name && url
            );

            return {
              Message: isAiGeneratedMessage ? null : message,
              BranchIds: branch || null,
              SocialIntegrationType:
                socialIntegrationType === DEFAULT_CHANNEL_INDEX
                  ? null
                  : socialIntegrationType || null,
              SocialIntegrationId:
                socialIntegrationId === DEFAULT_SOCIAL_INTEGRATION_ID_INDEX
                  ? null
                  : socialIntegrationId,
              AttachmentsIds: attachmentsIds,
              WeekSchedule: getScheduleDateForRobots(schedule || {}),
              Buttons: isInstagramChannel ? filledButtons : [],
            };
          default:
            return {};
        }
      };

      const typeParameters = await getTypeParameters();

      const requestData: NSalesFunnelsStagesApi.UpdateRobotRequest = {
        SalesFunnelStageId: Id,
        RobotType: +(selectedBotType?.id || 0),
        TriggerType: isKeywordMessageRobot
          ? KEYWORD_MESSAGE_TRIGGER_TYPE
          : trigger,
        EventTimeShift: +(deadline || 0),
        ...(CustomTimeShift !== null ? { CustomTimeShift } : {}),
        Comment: comment,
        TriggerParameters:
          getTriggerParameters() as NSalesFunnelsStagesApi.UpdateRobotRequest['TriggerParameters'],
        TypeParameters:
          typeParameters as NSalesFunnelsStagesApi.UpdateRobotRequest['TypeParameters'],
        AfterExecutionTags: afterExecutionTags || [],
        IgnoreSchedule:
          selectedBotType?.id === SET_TAG_ROBOT_INDEX
            ? isSetTagIncomingMessageRobot || Boolean(ignoreSchedule)
            : !useSchedule,
        ...((selectedBotType?.id === SEND_MESSAGE_ROBOT_INDEX ||
          isKeywordMessageRobot) && {
          AiPrompt: isAiGeneratedMessage ? aiPrompt || null : null,
        }),
      };

      if (botDetailsModalType === 'create') {
        // Бэк отклоняет ApplyToExistingLeads=true для AiClientSearch и
        // входящего сообщения (400) — явно фиксируем false, а не полагаемся
        // на undefined.
        await createBotHandler(
          requestData,
          trigger === 'AiClientSearch' || isSetTagIncomingMessageRobot
            ? false
            : applyToExistingLeads
        );

        return;
      }

      if (botDetailsModalType === 'edit') {
        if (!selectedBot?.bot.Id)
          return showNotification('Робот не найден', FRONT_WARNING);

        await updateBotHandler({ id: selectedBot?.bot.Id, data: requestData });
      }
    },
    [
      Id,
      selectedBotType?.id,
      botDetailsModalType,
      uploadAttachments,
      ActiveBranchId,
      showNotification,
      getScheduleDateForRobots,
      createBotHandler,
      selectedBot?.bot.Id,
      updateBotHandler,
    ]
  );

  const openEditBotHandler = useCallback(
    async (bot: NFunnels.SalesFunnelRobotDto) => {
      setIsEditBotModalLoading(true);
      setBotDetailsModalType('edit');
      const editBot = await getBotForIdHandler(bot.Id);

      if (!editBot) return;
      setSelectedBot({
        details: editBot,
        bot: bot,
      });
      const { RobotTypeName } = bot;
      const { RobotType, TypeParameters, AfterExecutionTags } = editBot;

      await getTriggersHandler(RobotType, bot.Id);
      await getTagsHandler();

      switch (String(RobotType)) {
        case TRANSITION_TO_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX:
          await getClientServicesHandler();
          break;
        case TRANSITION_FROM_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX:
          await getSalesFunnelStagesListHandler();
          break;
        case TRANSITION_FROM_THE_SALES_FUNNEL_ROBOT_INDEX:
          if (editBot.TypeParameters?.SaleFunnelId) {
            await getSalesFunnelStagesListHandler(
              Number(editBot.TypeParameters.SaleFunnelId)
            );
          }
          await getSalesFunnelsListHandler();
          break;
        case SET_TAG_ROBOT_INDEX: {
          const { TriggerParameters } = editBot;
          const triggerChannelId = TriggerParameters?.SocialIntegrationType
            ? `${TriggerParameters.SocialIntegrationType}`
            : undefined;
          const triggerIntegrationId = TriggerParameters?.SocialIntegrationId
            ? +TriggerParameters.SocialIntegrationId
            : undefined;

          await getSocialIntegrations({
            channelId: triggerChannelId,
            integrationId: triggerIntegrationId,
          });
          break;
        }
        case KEYWORD_MESSAGE_ROBOT_INDEX: {
          const keywordChannelId = TypeParameters?.SocialIntegrationType
            ? `${TypeParameters?.SocialIntegrationType}`
            : undefined;
          const keywordIntegrationId = TypeParameters?.SocialIntegrationId
            ? +TypeParameters?.SocialIntegrationId
            : undefined;

          await Promise.all([
            handleGetRobotFiles(bot.Id),
            getSocialIntegrations({
              channelId: keywordChannelId,
              integrationId: keywordIntegrationId,
            }),
          ]);
          break;
        }
        case SEND_MESSAGE_ROBOT_INDEX:
          const handledChannelId = TypeParameters?.SocialIntegrationType
            ? `${TypeParameters?.SocialIntegrationType}`
            : undefined;
          const handledIntegrationId = TypeParameters?.SocialIntegrationId
            ? +TypeParameters?.SocialIntegrationId
            : undefined;

          await Promise.all([
            handleGetRobotFiles(bot.Id),
            getSalesFunnelStagesListHandler(),
            getClientServicesHandler(),
            getEmployees(),
            getSocialIntegrations({
              channelId: handledChannelId,
              integrationId: handledIntegrationId,
              tagIds: AfterExecutionTags?.map(({ Id }) => Id),
            }),
            getBranchesHandler(),
          ]);
          break;
        case TASK_CREATING_ROBOT_INDEX:
          await getTaskUsersHandler();
          break;
        default:
          break;
      }
      openBotDetailsModal();
      setSelectedBotType({
        id: String(RobotType),
        title: RobotTypeName,
      });
      setIsEditBotModalLoading(false);
    },
    [
      getBotForIdHandler,
      getBranchesHandler,
      getClientServicesHandler,
      getEmployees,
      getSalesFunnelStagesListHandler,
      getSalesFunnelsListHandler,
      getSocialIntegrations,
      getTagsHandler,
      getTaskUsersHandler,
      getTriggersHandler,
      handleGetRobotFiles,
      openBotDetailsModal,
    ]
  );

  const handleDelete = useCallback(async () => {
    if (itemToDelete === 'stage') {
      try {
        await deleteSalesFunnelStage({
          id: stageFunnelData.Id,
          ActiveBranchId,
        }).unwrap();

        showNotification('Этап воронки продаж успешно удален', SUCCESS);

        setIsOpenDeleteModal(false);
        refetchGetSalesFunnelStages && (await refetchGetSalesFunnelStages());
      } catch (error) {
        showNotification(error);
      }
    } else if (itemToDelete === 'bot' && selectedBot) {
      deleteBotHandler(selectedBot.bot.Id);
    }
  }, [
    ActiveBranchId,
    deleteSalesFunnelStage,
    itemToDelete,
    refetchGetSalesFunnelStages,
    selectedBot,
    showNotification,
    stageFunnelData.Id,
    deleteBotHandler,
  ]);

  const openDeleteModal = useCallback((item: 'stage' | 'bot') => {
    setItemToDelete(item);
    setIsOpenDeleteModal(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsOpenDeleteModal(false);
    setItemToDelete(null);
  }, []);

  const handleEditIconClick = useCallback(() => {
    setCurrentSalesFunnelStage && setCurrentSalesFunnelStage(stageFunnelData);
    handleOpenSalesFunnelModal &&
      handleOpenSalesFunnelModal(Number(salesFunnelId), true);
  }, [
    handleOpenSalesFunnelModal,
    salesFunnelId,
    setCurrentSalesFunnelStage,
    stageFunnelData,
  ]);

  const handleAddIconClick = useCallback(() => {
    setCurrentSalesFunnelStage && setCurrentSalesFunnelStage(stageFunnelData);
    handleOpenSalesFunnelModal &&
      handleOpenSalesFunnelModal(Number(salesFunnelId), false);
  }, [
    handleOpenSalesFunnelModal,
    salesFunnelId,
    setCurrentSalesFunnelStage,
    stageFunnelData,
  ]);

  const isLoadingBots = useMemo(
    () =>
      robotsListTypesState.isLoading ||
      robotsListTypesState.isFetching ||
      robotsListTypesState.isUninitialized,
    [robotsListTypesState]
  );

  const isLoadingTriggers = useMemo(
    () =>
      robotsListTriggersState.isLoading ||
      robotsListTriggersState.isFetching ||
      robotsListTriggersState.isUninitialized ||
      triggerList.length === 0,
    [robotsListTriggersState, triggerList.length]
  );

  const botDetailsModalTitle =
    botDetailsModalType === 'create'
      ? `Добавить робота "${selectedBotType?.title}"`
      : `Редактировать робота "${selectedBot?.bot.RobotTypeName}"`;

  const isBotDetailsLoading = useMemo(
    () =>
      botDetailsModalType === 'create'
        ? robotsListTypesState.isLoading || robotsListTypesState.isFetching
        : isEditBotModalLoading,
    [robotsListTypesState, botDetailsModalType, isEditBotModalLoading]
  );

  return {
    isOpenDeleteModal,
    openDeleteModal,
    closeDeleteModal,
    handleDelete,
    itemToDelete,
    isOpenSelectTypeBotModal,
    openSelectTypeBotModal,
    closeSelectTypeBotModal,
    selectTypeBot,
    botTypeList,
    isLoadingBots,
    isOpenBotDetailsModal,
    closeBotDetailsModal,
    onSaveBotDetails,
    botDetailsModalTitle,
    triggerList,
    selectedBotTypeId: selectedBotType?.id,
    salesFunnelStagesList,
    servicesList,
    isLoadingTriggers,
    handleEditIconClick,
    handleAddIconClick,
    openEditBotHandler,
    stageFunnelData,
    deleteBotHandler,
    updateBotHandler,
    isBotDetailsLoading,
    selectedBot,
    botDetailsModalType,
    employees,
    socialIntegrations,
    tags,
    getTagsHandler,
    allBranches,
    selectedBotFiles,
    isBotDetailsModalDataLoading,
    salesFunnelsList,
    getSalesFunnelStagesListHandler,
    isUploadingAttachments,
    setIsUploadingAttachments,
    taskUsersList,
    IsStageCreate,
    IsStageEdit,
    IsStageDelete,
  };
};
